import type { PropsWithChildren } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

export const TabContext = createContext<{
  insertTab: (tab: string) => () => void
  selected: string
  tabs: string[]
  setSelected: (tab: string) => void
}>({
  insertTab: () => () => {},
  selected: '',
  tabs: [],
  setSelected: () => {},
})

export function TabContent({
  name,
  children,
}: PropsWithChildren<{ name: string }>) {
  const { selected, insertTab } = useContext(TabContext)

  useEffect(() => {
    const remove = insertTab(name)

    return remove
  }, [name, insertTab])

  if (selected === name) {
    return <>{children}</>
  }

  return null
}

export function useTabContext() {
  const [tabs, setTabs] = useState<string[]>([])
  const [selected, setSelected] = useState('')

  const insertTab = useCallback((tab: string, initial = false) => {
    setTabs(tabs => {
      if (tabs.includes(tab)) {
        console.warn(`Duplicate tab "${tab}"`)
        return [...tabs]
      }
      return [...tabs, tab]
    })

    return () => {
      setTabs(tabs => {
        const i = tabs.indexOf(tab)
        if (i < 0) {
          return tabs
        }

        return tabs.slice().splice(i, 1)
      })
    }
  }, [])

  useEffect(() => {
    if (tabs.length && !selected) {
      setSelected(tabs[0])
    }
  }, [tabs, selected])

  return useMemo(
    () => ({ tabs, insertTab, selected, setSelected }),
    [tabs, insertTab, selected, setSelected],
  )
}

export function UpgradeTabSwitch({
  children,
}: PropsWithChildren<{ onChange?: (tab: string) => void }>) {
  const { tabs, selected, setSelected } = useContext(TabContext)

  return (
    <div className="w-full">
      {tabs.length > 1 && (
        <div className="relative mx-auto w-full pt-12">
          <div className="grid w-full auto-cols-fr grid-flow-col border-b-2 border-light text-grey3">
            {tabs.map(tab => (
              <button
                className={`label-md row-start-1 flex h-8 items-start justify-center transition-all duration-100 ${selected === tab ? 'text-primary' : 'text-grey4'}`}
                onClick={() => setSelected(tab)}
                key={tab}
              >
                {tab}
              </button>
            ))}
            <div
              className="absolute bottom-0 row-start-2 h-[2px] bg-upgrade-tab-selection-line transition-all duration-100"
              style={{
                left: `${(100 / tabs.length) * tabs.indexOf(selected)}%`,
                width: `${100 / tabs.length}%`,
              }}
            />
          </div>
        </div>
      )}
      {children}
    </div>
  )
}
